import React, { useCallback, useEffect, useMemo, useRef } from 'react';
import { Flex } from 'antd';
import { Table } from 'components/Base/Table';
import useTable from 'components/Base/Table/hooks/useTable';
import {
  ETableStoragePaths,
  TColumnsState,
  TColumnType,
} from 'components/Base/Table/Table.types';
import useGetTableColumnData from 'components/Base/Table/components/SearchFilter';
import { TabBodyWrapper } from 'components/Base/Tabs';
import CardContent from 'components/Base/Layout/Content/CardContent';
import {
  ENotificationType,
  useShowToastNotification,
} from 'components/Base/Notification';
import ExportDropdown from 'components/Shared/ExportDropdown';
import { appLocalStorage, EStorageNames } from 'helpers/storage';
import {
  buildWorkshopTasksFilterParams,
  useExportWorkshopTasksMutation,
  useGetWorkshopTaskAssigneesQuery,
  useGetWorkshopTasksQuery,
} from 'redux/apiSlice/workshopTasks.slice';
import {
  EWorkshopPartType,
  EWorkshopTaskStatus,
  EWorkshopTasksFilterColumn,
  TWorkshopTaskUI,
  WORKSHOP_TASKS_DEFAULT_PAGE_SIZE,
  WORKSHOP_TASKS_DUE_DATE_SORT_FIELD,
} from 'data/types/workshopTasks.types';
import {
  SECTION_LABEL,
  SECTION_ORDER,
  sortTasksByPart,
} from './sortTasksByPart';
import WorkshopTaskStatusCell from './WorkshopTaskStatusCell';
import WorkshopTaskAssigneeCell from './WorkshopTaskAssigneeCell';
import WorkshopTaskDueDateCell from './WorkshopTaskDueDateCell';
import AddCustomTask from './AddCustomTask';
import EditCustomTask from './EditCustomTask';
import DeleteCustomTask from './DeleteCustomTask';

const STATUS_FILTER_OPTIONS = [
  { text: 'To Do', value: EWorkshopTaskStatus.TO_DO },
  { text: 'In Progress', value: EWorkshopTaskStatus.IN_PROGRESS },
  { text: 'Stalled', value: EWorkshopTaskStatus.STALLED },
  { text: 'Completed', value: EWorkshopTaskStatus.COMPLETED },
];

const PART_FILTER_OPTIONS = SECTION_ORDER.map(part => ({
  text: SECTION_LABEL[part],
  value: part,
}));

// GROW-4214: the client no longer assigns tasks to site coordinators, so the
// Assignee column is hidden rather than removed — flip this flag to restore
// it. The cell, filter, export mapping, and assignees query all stay wired.
const SHOW_ASSIGNEE_COLUMN = false;

// BE export column ids (ExportWorkshopTasksQueryHandler.Column) keyed by the
// table column keys persisted in tableSettings local storage. Order = CSV order.
const COLUMN_KEY_TO_EXPORT_ID: Record<string, string> = {
  taskName: 'task',
  [EWorkshopTasksFilterColumn.PART]: 'part',
  [WORKSHOP_TASKS_DUE_DATE_SORT_FIELD]: 'dueDate',
  [EWorkshopTasksFilterColumn.ASSIGNEE]: 'assignee',
  [EWorkshopTasksFilterColumn.STATUS]: 'status',
};

type WorkshopTasksTableProps = {
  workshopId: string;
};

const WorkshopTasksTable = ({ workshopId }: WorkshopTasksTableProps) => {
  // GROW-4338: set once useTable is up (below). The Actions column is built
  // first, so it reaches the selection setter through this stable callback.
  const pruneSelectionRef = useRef<((taskId: string) => void) | null>(null);
  const handleTaskDeleted = useCallback(
    (taskId: string) => pruneSelectionRef.current?.(taskId),
    [],
  );

  // No `= []` default here: that literal is a fresh array every render, which
  // would make assigneeFilterOptions/defaultColumns unstable and send the
  // setColumns effect below into an infinite update loop. Default inside the
  // memo instead so the reference stays stable until the query data changes.
  const { data: assigneeOptionsRaw } = useGetWorkshopTaskAssigneesQuery(
    { workshopId, assignee: '' },
    { skip: !workshopId || !SHOW_ASSIGNEE_COLUMN },
  );

  const assigneeFilterOptions = useMemo(
    () =>
      (assigneeOptionsRaw ?? []).map(option => ({
        text: option.name,
        value: option.name,
      })),
    [assigneeOptionsRaw],
  );

  const { getTableColumnSearchProps } = useGetTableColumnData();
  const taskSearchProps = useMemo(
    () => getTableColumnSearchProps<TWorkshopTaskUI>(),
    // getTableColumnSearchProps is recreated each render; the props are static.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  );

  const defaultColumns = useMemo<TColumnType<TWorkshopTaskUI>[]>(() => {
    const allColumns: TColumnType<TWorkshopTaskUI>[] = [
      {
        key: 'taskName',
        title: 'Task',
        dataIndex: 'taskName',
        ...taskSearchProps,
        render: (value: string) => <span>{value}</span>,
      },
      // GROW-4217: fixed widths on the trailing columns so the flexible Task
      // column absorbs the space freed by the hidden Assignee column — without
      // them, Due Date and Status stretch and their content floats in the gap.
      {
        key: EWorkshopTasksFilterColumn.PART,
        title: 'Part',
        dataIndex: 'part',
        width: 120,
        filters: PART_FILTER_OPTIONS,
        filterMultiple: true,
        render: (value: EWorkshopPartType) => (
          <span>{SECTION_LABEL[value]}</span>
        ),
      },
      {
        key: WORKSHOP_TASKS_DUE_DATE_SORT_FIELD,
        title: 'Due Date',
        dataIndex: 'dueDate',
        width: 170,
        sorter: true,
        render: (value: string, record: TWorkshopTaskUI) => (
          <WorkshopTaskDueDateCell
            workshopId={workshopId}
            taskId={record.id}
            value={value}
          />
        ),
      },
      {
        key: EWorkshopTasksFilterColumn.ASSIGNEE,
        title: 'Assignee',
        dataIndex: 'assignee',
        filters: assigneeFilterOptions,
        filterMultiple: false,
        render: (value: string, record: TWorkshopTaskUI) => (
          <WorkshopTaskAssigneeCell
            workshopId={workshopId}
            taskId={record.id}
            value={value}
          />
        ),
      },
      {
        key: EWorkshopTasksFilterColumn.STATUS,
        title: 'Status',
        // 150 = 95px tag + 8px gap + 16px chevron + 32px cell padding, so the
        // header filter trigger lines up with the chevron below it.
        dataIndex: 'status',
        width: 150,
        filters: STATUS_FILTER_OPTIONS,
        filterMultiple: true,
        render: (value: EWorkshopTaskStatus, record: TWorkshopTaskUI) => (
          <WorkshopTaskStatusCell
            workshopId={workshopId}
            taskId={record.id}
            value={value}
          />
        ),
      },
      // GROW-4278 / GROW-4338: Edit + Delete for custom tasks only. Per the PO,
      // having these controls is what marks a row as custom, so seeded rows
      // render nothing.
      // The blank title keeps the column out of the show/hide picker, and the
      // key has no export mapping, so it never reaches the CSV.
      {
        key: 'actions',
        title: ' ',
        dataIndex: 'actions',
        // 100 = two 32px icon buttons + 4px gap + 32px cell padding.
        width: 100,
        align: 'end',
        render: (_: unknown, record: TWorkshopTaskUI) =>
          record.custom ? (
            <Flex gap={4} justify="flex-end">
              <EditCustomTask
                workshopId={workshopId}
                taskId={record.id}
                taskName={record.taskName}
              />
              <DeleteCustomTask
                workshopId={workshopId}
                taskId={record.id}
                taskName={record.taskName}
                onDeleted={handleTaskDeleted}
              />
            </Flex>
          ) : null,
      },
    ];
    return SHOW_ASSIGNEE_COLUMN
      ? allColumns
      : allColumns.filter(
          column => column.key !== EWorkshopTasksFilterColumn.ASSIGNEE,
        );
  }, [workshopId, assigneeFilterOptions, taskSearchProps, handleTaskDeleted]);

  const storageKey = `${ETableStoragePaths.WORKSHOP_TASKS}-${workshopId}`;

  const {
    columns,
    sortColumn,
    selectedFilters,
    handleTableChange,
    hasSelectedFilter,
    onFiltersReset,
    setColumns,
    selectedRows,
    setSelectedRows,
  } = useTable<TWorkshopTaskUI>({
    storagePath: storageKey,
    defaultColumns,
  });

  // useTable snapshots defaultColumns into state once, so the Assignee filter
  // options (loaded asynchronously from getWorkshopTaskAssignees) never reach
  // the rendered columns. Re-sync whenever the columns recompute, matching the
  // RegistrantsList pattern. derivedColumns re-applies the active filter/sort
  // on top, so this preserves any selection.
  useEffect(() => {
    setColumns(defaultColumns);
  }, [defaultColumns, setColumns]);

  // A deleted task must not linger in the Export Selected selection, or its
  // stale id would be sent to the export endpoint.
  pruneSelectionRef.current = (taskId: string) =>
    setSelectedRows(prev => ({
      selectedRowsKeys: prev.selectedRowsKeys.filter(key => key !== taskId),
      selectedRowsRecords: prev.selectedRowsRecords.filter(
        record => record.id !== taskId,
      ),
    }));

  // A pre-GROW-4214 assignee filter can still be persisted in tableSettings;
  // drop it while the column is hidden so it can't invisibly narrow the list.
  const filterParams = useMemo(() => {
    const params = buildWorkshopTasksFilterParams(selectedFilters);
    if (!SHOW_ASSIGNEE_COLUMN) {
      delete params.assignee;
    }
    return params;
  }, [selectedFilters]);

  const { data, isLoading, isFetching } = useGetWorkshopTasksQuery(
    {
      workshopId,
      sortField: sortColumn?.sortField,
      sortDirection: sortColumn?.sortDirection as 'ASC' | 'DESC' | undefined,
      ...filterParams,
    },
    { skip: !workshopId },
  );

  const tasks = data?.content ?? [];
  const totalElements = data?.totalElements ?? 0;
  // Task search is client-side: the list API has no name param, and the spec
  // keeps all tasks on one page (page size 50), so the full set is already loaded.
  const taskSearch = String(selectedFilters?.taskName?.[0] ?? '')
    .trim()
    .toLowerCase();

  // Spec §3.2: one stacked list ordered Prep -> 1 -> 2 -> 3 -> Post.
  // A column sort (Due Date) is server-side, so honor server order when it is active.
  const orderedTasks = useMemo(() => {
    const sorted = sortColumn?.sortField ? tasks : sortTasksByPart(tasks);
    if (!taskSearch) return sorted;
    return sorted.filter(task =>
      task.taskName.toLowerCase().includes(taskSearch),
    );
  }, [tasks, sortColumn?.sortField, taskSearch]);

  const { showToastNotification } = useShowToastNotification();
  const [exportWorkshopTasks, { isLoading: isExporting }] =
    useExportWorkshopTasksMutation();

  const getVisibleExportColumns = useCallback(() => {
    const tableSettings = appLocalStorage.getItem(EStorageNames.tableSettings);
    const columnsState = tableSettings?.[storageKey] as TColumnsState[];
    const exportIds = !columnsState
      ? Object.values(COLUMN_KEY_TO_EXPORT_ID)
      : columnsState
          .filter(col => col.show)
          .map(col => COLUMN_KEY_TO_EXPORT_ID[col.key as string])
          .filter(Boolean);
    // Stored column state may predate GROW-4214 and still mark assignee as
    // shown, so the hidden column has to be stripped here as well.
    return SHOW_ASSIGNEE_COLUMN
      ? exportIds
      : exportIds.filter(
          id =>
            id !== COLUMN_KEY_TO_EXPORT_ID[EWorkshopTasksFilterColumn.ASSIGNEE],
        );
  }, [storageKey]);

  // Both modes go through POST /workshops/tasks/export (GROW-4164 design
  // note: unlike Attendance, Export Selected is also server-side so one
  // handler owns the CSV formatting). taskSearch is lowercased, which is
  // safe to send: the BE taskName filter lowercases both sides.
  const runExport = useCallback(
    async (resourceIds?: string[]) => {
      try {
        const sort =
          sortColumn?.sortField && sortColumn?.sortDirection
            ? {
                field: sortColumn.sortField,
                direction: sortColumn.sortDirection as 'ASC' | 'DESC',
              }
            : undefined;
        await exportWorkshopTasks({
          columns: getVisibleExportColumns(),
          filters: {
            workshopId,
            ...filterParams,
            ...(taskSearch && { taskName: taskSearch }),
            ...(resourceIds?.length && { resourceIds }),
          },
          ...(sort && { sort }),
        }).unwrap();
      } catch {
        showToastNotification({
          message: 'Export failed. Please try again.',
          type: ENotificationType.ERROR,
        });
      }
    },
    [
      sortColumn?.sortField,
      sortColumn?.sortDirection,
      exportWorkshopTasks,
      getVisibleExportColumns,
      workshopId,
      filterParams,
      taskSearch,
      showToastNotification,
    ],
  );

  const handleExportAll = useCallback(() => runExport(), [runExport]);

  const handleExportSelected = useCallback(() => {
    const { selectedRowsKeys } = selectedRows;
    if (!selectedRowsKeys?.length) {
      showToastNotification({
        message: 'Please select at least one row to export',
        type: ENotificationType.ERROR,
      });
      return;
    }
    runExport(selectedRowsKeys.map(key => String(key)));
  }, [selectedRows, runExport, showToastNotification]);

  const exportAction = useMemo(
    () => (
      <ExportDropdown
        key="export-actions"
        onExportAll={handleExportAll}
        onExportSelected={handleExportSelected}
        isExporting={isExporting}
      />
    ),
    [handleExportAll, handleExportSelected, isExporting],
  );

  // GROW-4215: custom one-off tasks for this workshop, right of Export.
  const addTaskAction = useMemo(
    () => <AddCustomTask key="add-custom-task" workshopId={workshopId} />,
    [workshopId],
  );

  // Standard table pattern (see RegistrantsList): keep the Table mounted so its
  // built-in "Reset filters" button stays available whenever a filter/sort is
  // active — including when the result set is empty or the request fails. Only
  // the no-filter empty / initial-load states short-circuit to the empty view.
  return (
    <TabBodyWrapper
      isEmpty={!tasks.length && !hasSelectedFilter && !isFetching}
      isLoading={isLoading}
      emptyText="No tasks yet."
    >
      <CardContent tabContent contentPadding="16px">
        <Table<TWorkshopTaskUI>
          tableTitle="Tasks"
          storagePath={storageKey}
          // GROW-4217: "auto" lets the browser stretch the fixed-width columns
          // with the space freed by the hidden Assignee column, pushing the
          // sort/filter triggers away from their cells' content. "fixed"
          // honors the widths exactly; Task (no width) absorbs the leftover.
          tableLayout="fixed"
          columns={columns}
          dataSource={orderedTasks}
          loading={isFetching}
          rowKey={record => record.id}
          totalDataCount={totalElements}
          onChange={handleTableChange}
          hasFilters={hasSelectedFilter}
          onFiltersReset={onFiltersReset}
          // Export first, Add last — same order as the Outputs tab, so the
          // primary "add" action sits at the far right of the header.
          headerActions={[exportAction, addTaskAction]}
          selectedRows={selectedRows}
          setSelectedRows={setSelectedRows}
          pagination={
            totalElements > WORKSHOP_TASKS_DEFAULT_PAGE_SIZE
              ? { pageSize: WORKSHOP_TASKS_DEFAULT_PAGE_SIZE }
              : false
          }
          onRow={() =>
            ({
              'data-testid': 'task-row',
            } as React.HTMLAttributes<HTMLElement>)
          }
          data-cy="workshop-tasks-table"
        />
      </CardContent>
    </TabBodyWrapper>
  );
};

export default WorkshopTasksTable;
